import { useState, useEffect } from 'react'
import {
  Box,
  Text,
  Editable,
  IconButton,
  Spinner,
} from '@chakra-ui/react'
import { Check, X } from 'lucide-react'

export interface EditableFieldProps {
  label: string
  value: string | number | null | undefined
  fieldName: string
  placeholder?: string
  type?: 'text' | 'number'
  disabled?: boolean
  onUpdate: (fieldName: string, value: string | number) => Promise<void>
}

export function EditableField({
  label,
  value,
  fieldName,
  placeholder = 'Non renseigné',
  type = 'text',
  disabled = false,
  onUpdate,
}: EditableFieldProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [currentValue, setCurrentValue] = useState(value?.toString() || '')

  useEffect(() => {
    setCurrentValue(value?.toString() || '')
  }, [value])

  const handleSubmit = async (details: { value: string }) => {
    const newValue = details.value
    if (newValue !== (value?.toString() || '')) {
      setIsLoading(true)
      try {
        const finalValue = type === 'number' && newValue ? Number(newValue) : newValue
        await onUpdate(fieldName, finalValue)
      } catch (error) {
        console.error('Error updating field:', error)
      } finally {
        setIsLoading(false)
      }
    }
  }

  return (
    <Box>
      <Text fontSize="sm" color="gray.600" mb={1}>
        {label}
      </Text>
      <Editable.Root
        value={currentValue}
        onValueChange={(e) => setCurrentValue(e.value)}
        onValueCommit={handleSubmit}
        placeholder={placeholder}
        disabled={isLoading || disabled}
      >
        <Editable.Preview
          fontWeight="medium"
          color={currentValue ? 'inherit' : 'gray.400'}
          px={2}
          py={1}
          borderRadius="md"
          display="flex"
          alignItems="center"
          gap={2}
          _hover={{ bg: disabled ? undefined : 'gray.50' }}
          cursor={disabled ? 'default' : 'pointer'}
        />
        <Editable.Input px={2} py={1} type={type} />
        <Editable.Control display="flex" gap={1} alignItems="center">
          {isLoading ? (
            <Spinner size="sm" />
          ) : (
            <>
              <Editable.SubmitTrigger asChild>
                <IconButton variant="ghost" size="xs" colorPalette="green" aria-label="Valider">
                  <Check size={14} />
                </IconButton>
              </Editable.SubmitTrigger>
              <Editable.CancelTrigger asChild>
                <IconButton variant="ghost" size="xs" colorPalette="red" aria-label="Annuler">
                  <X size={14} />
                </IconButton>
              </Editable.CancelTrigger>
            </>
          )}
        </Editable.Control>
      </Editable.Root>
    </Box>
  )
}
